import { AgentCategory } from "~/shared/enums";
import type { AgentConfig, ValidationFunction } from "~/shared/types";
import { isValidUrlIncludingLocal, makeValidateRegex, validateIsNotEmpty } from "~/utils/validation";

import AgentIcon from "./AgentIcon.vue";
import Icon from "./Icon.vue";

const GATEWAY_HTTP_IP_URL_REGEX = /^https:\/\/\d{1,3}(?:\.\d{1,3}){3}(?::\d{1,5})?(?:[/?#].*)?$/;
const AGENT_ID_REGEX = /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/;
const isValidGatewayIpUrl = (value: string) => GATEWAY_HTTP_IP_URL_REGEX.test(value.trim());

const validateGatewayUrl: ValidationFunction = (value) =>
  isValidUrlIncludingLocal(value) || isValidGatewayIpUrl(value)
    ? { isValid: true }
    : { isValid: false, error: "Enter a valid URL" };

const validateAgentId = makeValidateRegex(AGENT_ID_REGEX, "Enter a valid OpenClaw agent ID", true);

const config: AgentConfig = {
  name: "OpenClaw",
  category: AgentCategory.ASSISTANT,
  icon: Icon,
  agentIcon: AgentIcon,
  color: "#dc2626",
  description: "Keep project context in memory and run automated follow-ups, reminders, and recurring updates",
  descriptionMarkdown: `## Overview

Delegate work to OpenClaw directly from Dart. This can also work with OpenClaw-based agents like Nemoclaw and OpenShell. When you assign a task, Dart sends the task prompt to OpenClaw and posts the reply back into the task. Later comments on that same task continue the same OpenClaw session instead of starting over.

Dart also supports attaching images and documents to tasks. Unsupported types are skipped automatically.

## Setup guide

1. Host OpenClaw somewhere Dart can reach
2. In OpenClaw, go to Settings > Infrastructure and configure the following:
   - Set **Gateway auth mode** to token or password
   - Set the **Gateway token** or **Gateway password** field and copy the value
   - Under **Gateway HTTP API > Gateway HTTP Endpoints > Responses**, enable the **Enabled** toggle
3. Open Dart settings, go to Agents, and choose OpenClaw
4. Paste your server URL and token or password
5. Optionally set a specific agent ID if you don't want the default
6. Assign a task to OpenClaw from Dart`,
  configFields: [
    {
      key: "gatewayUrl",
      type: "text",
      label: "Gateway URL",
      placeholder: "https://openclaw.example",
      validate: validateGatewayUrl,
    },
    {
      key: "authToken",
      type: "password",
      label: "Gateway token or password",
      placeholder: "Your OpenClaw gateway secret",
      validate: validateIsNotEmpty,
    },
    {
      key: "agentId",
      type: "text",
      label: "Agent ID",
      placeholder: "main",
      optional: true,
      validate: validateAgentId,
    },
  ],
};

export default config;
